class FeaturedCollectionSliderManager extends HTMLElement {
  constructor() {
    super();
    this.activeSlider = null;
    this.sliders = new Map();
    this.init();
  }

  connectedCallback() {
    this.setupEventListeners();
    this.initializeActiveSlider();
  }

  init() {
    this.sliderWrappers = this.querySelectorAll('.featured-collection__slider-wrapper');
    this.navButtons = this.querySelectorAll('.section-header__nav');
    const sectionRoot = this.closest('[data-section-id]');
    this.sectionId = sectionRoot ? sectionRoot.dataset.sectionId : null;
  }

  setupEventListeners() {
    this.navButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const collectionHandle = button.dataset.collection;
        this.switchToSlider(collectionHandle);
      });
    });
  }

  initializeActiveSlider() {
    const activeSlider = this.querySelector('.featured-collection__slider-wrapper--active');
    if (activeSlider) this.initializeSlider(activeSlider);
  }

  switchToSlider(collectionHandle) {
    this.sliderWrappers.forEach((slider) => {
      slider.classList.remove('featured-collection__slider-wrapper--active');
      this.destroySlider(slider);
    });

    this.navButtons.forEach((button) => {
      button.classList.toggle('section-header__nav--active', button.dataset.collection === collectionHandle);
    });

    const targetSlider = document.getElementById(collectionHandle);
    if (!targetSlider) return;

    targetSlider.classList.add('featured-collection__slider-wrapper--active');

    if (!targetSlider.dataset.sectionId) {
      const sectionRoot = targetSlider.closest('[data-section-id]');
      if (sectionRoot) targetSlider.dataset.sectionId = sectionRoot.dataset.sectionId || '';
    }

    if (targetSlider.dataset.ajaxLoad === 'true') {
      this.loadSliderFromSectionAPI(targetSlider);
    } else {
      setTimeout(() => this.initializeSlider(targetSlider), 50);
    }
  }

  initializeSlider(sliderElement) {
    const sliderId = sliderElement.id;
    if (this.sliders.has(sliderId)) this.destroySlider(sliderElement);

    const flickityOptions = { draggable: true, pageDots: false, cellAlign: 'left', wrapAround: true, lazyLoad: false };
    if (typeof Flickity === 'undefined') return;

    const flickityInstance = new Flickity(sliderElement, flickityOptions);
    this.sliders.set(sliderId, flickityInstance);
  }

  destroySlider(sliderElement) {
    const sliderId = sliderElement.id;
    if (!this.sliders.has(sliderId)) return;
    try {
      this.sliders.get(sliderId).destroy();
    } catch (_) {}
    this.sliders.delete(sliderId);
  }

  setActiveNavByHandle(handle) {
    this.navButtons.forEach((button) => {
      button.classList.toggle('section-header__nav--active', button.dataset.collection === handle);
    });
  }

  fallbackToFirstAvailableSlider(currentSlider) {
    let fallbackHandle = null;
    if (this.sliders.size > 0) {
      const firstSliderId = this.sliders.keys().next().value;
      fallbackHandle = firstSliderId;
    } else if (this.sliderWrappers.length > 0) {
      fallbackHandle = this.sliderWrappers[0].id;
    }

    if (!fallbackHandle) return;

    this.sliderWrappers.forEach((sliderWrapper) => sliderWrapper.classList.remove('featured-collection__slider-wrapper--active'));
    const fallbackSlider = document.getElementById(fallbackHandle);
    if (!fallbackSlider) return;

    fallbackSlider.classList.add('featured-collection__slider-wrapper--active');
    this.setActiveNavByHandle(fallbackHandle);

    if (!this.sliders.has(fallbackHandle)) {
      if (fallbackSlider.dataset.ajaxLoad === 'true') {
        fallbackSlider.removeAttribute('data-ajax-load');
      }
      this.initializeSlider(fallbackSlider);
    }
  }

  async loadSliderFromSectionAPI(sliderElement) {
    const sectionRoot = sliderElement.closest('[data-section-id]');
    const sectionId = sectionRoot ? sectionRoot.dataset.sectionId : null;
    const collectionHandle = sliderElement.dataset.collectionHandle;
    const productLimit = sliderElement.dataset.productLimit;
    const count = sliderElement.dataset.count;

    if (!sectionId) return this.fallbackToFirstAvailableSlider(sliderElement);

    try {
      const sectionHandle = 'featured-collection-slider';
      const queryParams = new URLSearchParams({
        collection: collectionHandle || '',
        limit: String(productLimit || ''),
        count: String(count || '')
      });
      const apiUrl = `${window.location.pathname}?sections=${encodeURIComponent(sectionHandle)}&${queryParams.toString()}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      });

      if (!response.ok) throw new Error('bad_status');

      const responseData = await response.json();
      const sectionHTML = responseData[sectionHandle];
      if (!sectionHTML) throw new Error('empty_html');

      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = sectionHTML;
      const freshSlider = tempContainer.querySelector(`#${CSS && CSS.escape ? CSS.escape(sliderElement.id) : sliderElement.id}`);

      const wasActive = sliderElement.classList.contains('featured-collection__slider-wrapper--active');

      if (!freshSlider) {
        sliderElement.innerHTML = sectionHTML;
        if (wasActive) sliderElement.classList.add('featured-collection__slider-wrapper--active');
      } else {
        sliderElement.replaceWith(freshSlider);
        if (wasActive) freshSlider.classList.add('featured-collection__slider-wrapper--active');
        sliderElement = freshSlider;
      }

      sliderElement.removeAttribute('data-ajax-load');
      this.sliderWrappers = this.querySelectorAll('.featured-collection__slider-wrapper');
      sliderElement = document.getElementById(sliderElement.id) || sliderElement;

      setTimeout(() => this.initializeSlider(sliderElement), 60);
      new theme.QuickAdd(sliderElement);
    } catch (_) {
      this.fallbackToFirstAvailableSlider(sliderElement);
    }
  }

  disconnectedCallback() {
    this.sliders.forEach((flickityInstance) => {
      try { flickityInstance.destroy(); } catch (_) {}
    });
    this.sliders.clear();
  }
}

customElements.define('featured-collection-slider-manager', FeaturedCollectionSliderManager);
